(() => {
  const categories = new Set(['all', 'knives', 'hand-tools', 'outdoor-utility', 'components-hardware']);
  const categoryFrom = (url) => categories.has(url.searchParams.get('category')) ? url.searchParams.get('category') : 'all';
  async function getDocument(url) {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw new Error('Unavailable');
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }
  function initLibrary(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const grid = root.querySelector('[data-design-grid]');
    const status = root.querySelector('[data-design-status]');
    const filters = root.querySelector('[data-design-filters]');
    const empty = root.querySelector('[data-design-empty]');
    const failure = root.querySelector('[data-design-failure]');
    const pagination = root.querySelector('[data-design-pagination]');
    const feed = root.querySelector('[data-design-feed]');
    const native = root.querySelector('[data-design-native-pagination]');
    const pageSize = Number(root.dataset.pageSize) || 12;
    let cards = [], ready = false, busy = false;
    filters.hidden = false;
    function highlight() {
      const category = categoryFrom(new URL(location.href));
      filters.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.category === category)));
      return category;
    }
    function render(focus = false) {
      const category = highlight();
      if (!ready) return;
      const matches = cards.filter(card => category === 'all' || card.dataset.category === category);
      const pages = Math.max(1, Math.ceil(matches.length / pageSize));
      const url = new URL(location.href);
      const requested = Number(url.searchParams.get('od_page'));
      const page = Math.min(pages, Math.max(1, Number.isSafeInteger(requested) ? requested : 1));
      grid.replaceChildren(...matches.slice((page - 1) * pageSize, page * pageSize).map(card => card.cloneNode(true)));
      status.textContent = root.dataset.count.replace('{count}', String(matches.length));
      grid.hidden = matches.length === 0;
      empty.hidden = matches.length !== 0;
      pagination.hidden = pages <= 1;
      pagination.querySelector('[data-design-prev]').disabled = page <= 1;
      pagination.querySelector('[data-design-next]').disabled = page >= pages;
      pagination.querySelector('[data-design-page]').textContent = `${page} / ${pages}`;
      root.dataset.currentPage = String(page);
      if (focus) status.focus({ preventScroll: true });
    }
    function navigate(category, page = 1) {
      const url = new URL(location.href);
      if (category === 'all') url.searchParams.delete('category');
      else url.searchParams.set('category', category);
      url.searchParams.delete(feed.dataset.pageParam || 'page');
      if (page > 1) url.searchParams.set('od_page', String(page));
      else url.searchParams.delete('od_page');
      history.pushState(null, '', url);
      render(true);
    }
    filters.addEventListener('click', event => {
      const button = event.target.closest('[data-category]');
      if (button) navigate(button.dataset.category);
    });
    root.querySelector('[data-design-reset]').addEventListener('click', () => navigate('all'));
    pagination.querySelector('[data-design-prev]').addEventListener('click', () => navigate(highlight(), Number(root.dataset.currentPage) - 1));
    pagination.querySelector('[data-design-next]').addEventListener('click', () => navigate(highlight(), Number(root.dataset.currentPage) + 1));
    window.addEventListener('popstate', () => { if (root.isConnected) render(); });
    async function load() {
      if (busy) return;
      busy = true; ready = false; failure.hidden = true; empty.hidden = true; pagination.hidden = true;
      grid.hidden = true; native.hidden = true;
      root.setAttribute('aria-busy', 'true');
      status.textContent = root.dataset.loading;
      filters.querySelectorAll('button').forEach(button => { button.disabled = true; });
      highlight();
      try {
        let current = feed;
        const first = new URL(location.href);
        first.searchParams.set(feed.dataset.pageParam || 'page', '1');
        first.searchParams.set('section_id', root.dataset.sectionId);
        first.searchParams.delete('od_page');
        if (new URL(location.href).searchParams.has(feed.dataset.pageParam || 'page')) {
          current = (await getDocument(first)).querySelector('[data-design-feed]');
        }
        const records = new Map(), visited = new Set();
        while (current) {
          if (Number(current.dataset.total) > 25000) throw new Error('Pagination limit');
          current.querySelectorAll('[data-design-card]').forEach(card => {
            if (categories.has(card.dataset.category) && card.dataset.category !== 'all') records.set(card.dataset.key, card);
          });
          if (!current.dataset.next) break;
          const next = new URL(current.dataset.next, location.href);
          if (next.origin !== location.origin || next.pathname !== location.pathname) throw new Error('Invalid pagination');
          next.searchParams.set('section_id', root.dataset.sectionId);
          if (visited.has(next.href)) throw new Error('Repeated pagination');
          visited.add(next.href);
          current = (await getDocument(next)).querySelector('[data-design-feed]');
          if (!current) throw new Error('Missing feed');
        }
        if (!current) throw new Error('Missing feed');
        cards = [...records.values()].sort((a, b) => (Number(a.dataset.order) - Number(b.dataset.order)) || a.dataset.reference.localeCompare(b.dataset.reference));
        ready = true;
        const url = new URL(location.href);
        if (url.searchParams.has('category') && !categories.has(url.searchParams.get('category'))) {
          url.searchParams.delete('category'); url.searchParams.delete('od_page'); history.replaceState(null, '', url);
        }
        render();
      } catch {
        status.textContent = root.dataset.error;
        failure.hidden = false;
        native.hidden = false;
      } finally {
        busy = false; root.removeAttribute('aria-busy');
        filters.querySelectorAll('button').forEach(button => { button.disabled = !ready; });
      }
    }
    root.querySelector('[data-design-retry]').addEventListener('click', load);
    load();
  }
  function initInquiry(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const selected = new URL(location.href).searchParams.get('design');
    if (!selected) return;
    const form = root.closest('form');
    const status = root.querySelector('[data-design-inquiry-status]');
    const summary = root.querySelector('[data-design-summary]');
    const retry = root.querySelector('[data-design-inquiry-retry]');
    const fields = [...root.querySelectorAll('input[data-design-field]')];
    let state = 'pending', validSubmission = false, submitting = false, revision = 0;
    root.hidden = false;
    function clearFields() { fields.forEach(input => { input.value = ''; input.disabled = true; }); }
    async function resolve() {
      const requestRevision = ++revision;
      state = 'pending'; summary.hidden = true; retry.hidden = true; clearFields();
      status.textContent = root.dataset.loading;
      try {
        const url = new URL(selected, location.origin);
        const base = root.dataset.root || '/';
        const pagePrefix = base.endsWith('/') ? `${base}pages/` : `${base}/pages/`;
        if (url.origin !== location.origin || url.username || url.password || !url.pathname.startsWith(pagePrefix) || url.search || url.hash || /%2f|%5c/i.test(url.pathname)) throw new Error('Invalid design URL');
        const document = await getDocument(url);
        if (requestRevision !== revision) return false;
        const node = document.querySelector('[data-oem-open-design-record]');
        if (!node) throw new Error('Not a public design');
        const record = JSON.parse(node.textContent);
        const canonical = new URL(record.url, location.origin);
        if (canonical.origin !== location.origin || canonical.pathname !== url.pathname || !record.reference || !record.name || !record.handle || !categories.has(record.category) || record.category === 'all' || record.source !== 'Open Design') throw new Error('Invalid public record');
        const values = { design_reference: record.reference, design_name: record.name, design_category: record.categoryLabel, design_detail_url: canonical.href, inquiry_source: 'Open Design' };
        fields.forEach(input => { input.value = values[input.dataset.designField]; input.disabled = false; });
        summary.querySelector('[data-design-name]').textContent = record.name;
        summary.querySelector('[data-design-reference]').textContent = record.reference;
        summary.querySelector('[data-design-category-label]').textContent = record.categoryLabel;
        summary.querySelector('a').href = canonical.href;
        summary.hidden = false; status.textContent = root.dataset.ready; state = 'valid';
        return true;
      } catch {
        if (requestRevision !== revision) return false;
        clearFields(); state = 'invalid'; status.textContent = root.dataset.error; retry.hidden = false;
        return false;
      }
    }
    retry.addEventListener('click', resolve);
    root.querySelector('[data-design-clear]').addEventListener('click', () => {
      revision++;
      state = 'general'; clearFields(); root.hidden = true;
      const url = new URL(location.href); url.searchParams.delete('design'); history.replaceState(null, '', url);
    });
    form.addEventListener('submit', async event => {
      if (state === 'general' || validSubmission) return;
      event.preventDefault();
      // Only the revalidated native submit should reach downstream submit hooks.
      event.stopImmediatePropagation();
      if (submitting) return;
      submitting = true;
      const submitter = event.submitter;
      if (await resolve()) {
        validSubmission = true;
        try { form.requestSubmit(submitter || undefined); } finally { validSubmission = false; }
      } else if (state !== 'general') status.focus();
      submitting = false;
    }, { capture: true });
    resolve();
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-oem-design-library]').forEach(initLibrary);
    scope.querySelectorAll('[data-oem-design-inquiry]').forEach(initInquiry);
  }
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
